/*
 * Canonical N-way experiment registry — deferred asset.
 *
 * Does NO assignment. All bucketing + the cookie write + the reveal-class set
 * happen synchronously in the inline snippet
 * (theme/snippets/experiment-init.liquid) BEFORE first paint. This deferred
 * asset only READS the class that snippet set and RIDES the existing
 * consent-gated CTA→GA4 path — it never assigns, never emits a new path.
 *
 * Generalizes the Phase-1 single-global asset (theme/assets/pdp-experiment.js —
 * since RETIRED when that PDP buy-box A/B concluded 2026-08-20). Where Phase 1
 * exposed one hardcoded PDP global, this
 * exposes a KEYED registry so many experiments coexist on distinct keys:
 *
 *   window.NCExperiments = {
 *     get(key)                  -> { variant }   // effective (rendered) variant, or undefined
 *     active()                  -> [{ key, variant }, …]   // every running experiment
 *     emitView(key, extra)      -> void          // the experiment_view denominator
 *     tagVariant(key, payload)  -> payload        // extend a payload with the generic pair
 *   }
 *
 * Measurement contract (resolved "GA4 dimension shape") — the SLOT-POOL model
 * (rules/experiment_slots.md): every experiment emits the canonical
 * `experiment_view` denominator + tags its conversion events with the GA4
 * dimension PAIR of the SLOT its key occupies. Each active key resolves via
 * `window.NC_EXP_SLOTS` (the generated projection map) to its slot's
 * `{id_dim, variant_dim}` — slot 0 = the legacy `experiment_id`/`experiment_variant`
 * pair; slot K = `exp_slotK_id`/`exp_slotK_variant`. Because concurrent
 * experiments occupy DISTINCT slots (distinct dim pairs), several can run on the
 * same page-scope without a variant collision. bin/experiment-report.py filters
 * each read by `<the slot's id_dim> == key`.
 *
 * SOLE emit path: window.NCCtaTracking.publish (cta-tracking-core.js:90 — the
 * shared userCanBeTracked()-gated 3-sink chain). This asset NEVER re-inlines that
 * chain, NEVER adds a page-context analytics script tag, and NEVER calls the
 * Shopify analytics publish sink itself — it only REFERENCES it as a `typeof … ===
 * 'function'` readiness predicate (the exact pattern Phase-1's trackingReady()
 * uses), per rules/canonical_centralization.md + rules/cookie_consent_convention.md.
 *
 * The `mission_click`-fired-0× lesson: a published `experiment_view` reaches GA4
 * ONLY if the Customer Events pixel ALSO subscribes to it. That pixel subscription
 * + the ONE-TIME registration of the pool's slot dimensions in GA4 Admin (the 2N
 * `exp_slotK_*` dims + slot 0's legacy pair) are DOCUMENTED in
 * rules/experiment_slots.md § One-time setup and wired once — do NOT assume they
 * exist for a given experiment; the report's `--preflight` gate catches a dead sink.
 */
(function () {
  'use strict';

  // Idempotent install — survives an accidental double-include (theme editor
  // reloads, PageFly wraps) without double-registering the consent listener.
  if (window.NCExperiments) return;

  // ─── Reading the effective (rendered) variant from the reveal class ───
  // Chunk A's inline snippet set `html.exp-{key}-{variant}` synchronously before
  // first paint, encoding any `force`/kill. Reading the CLASS (not the raw
  // `nc_exp_{key}` cookie) means the reported variant always matches what the
  // visitor actually SEES.
  //
  // The class token is exactly `exp-{key}-{variant}` where key and variant both
  // match [a-z0-9_]+ (validated by the snippet). Neither contains a hyphen, so
  // the trailing '-' in the prefix below is the unambiguous key↔variant boundary:
  // a key that is a prefix of another key (e.g. `col` vs `col_layout`) can never
  // cross-match, because the char after the shorter key is '_' (or another token
  // char), never the '-' the prefix requires.
  function effectiveVariant(key) {
    var prefix = 'exp-' + key + '-';
    var cl = document.documentElement.classList;
    for (var i = 0; i < cl.length; i++) {
      var c = cl[i];
      if (c.indexOf(prefix) === 0) {
        return c.slice(prefix.length);
      }
    }
    return undefined; // no exp-{key}-* class → the experiment is not running here
  }

  // get(key) -> { variant }. `variant` is the effective variant, or undefined when
  // the experiment is not running on this surface (the class is absent). A caller
  // MUST guard on `NCExperiments.get(key).variant` before reading it.
  function get(key) {
    return { variant: effectiveVariant(key) };
  }

  // ─── active(): key-discovery primitive ───
  // Returns [{ key, variant }, …] for every `exp-{key}-{variant}` class present on
  // <html>. Lets a GLOBAL theme-side tracker (e.g. audible-tracking.js, which
  // fires on any page) tag its emit with the page's active experiment WITHOUT
  // hardcoding a key — iterate active() and tagVariant(key, payload) each. For
  // NC's "~one experiment per page-scope" reality this has ≤1 entry per scope.
  //
  // The regex is a FIXED constant (no key interpolation → no injection). Because
  // neither key nor variant contains a hyphen, `exp-{key}-{variant}` has exactly
  // one key↔variant '-', so the two greedy [a-z0-9_]+ groups split unambiguously;
  // a malformed multi-hyphen class does not match.
  var CLASS_RE = /^exp-([a-z0-9_]+)-([a-z0-9_]+)$/;
  function active() {
    var out = [];
    var cl = document.documentElement.classList;
    for (var i = 0; i < cl.length; i++) {
      var m = CLASS_RE.exec(cl[i]);
      if (m) out.push({ key: m[1], variant: m[2] });
    }
    return out;
  }

  // ─────────────────────────── SLOT POOL ───────────────────────────
  // GA4 dimension slot pool (rules/experiment_slots.md). Each active
  // experiment key resolves to a pre-registered GA4 dimension PAIR (a
  // "slot") so many experiments — same OR different page-scope — coexist
  // WITHOUT colliding on one shared param pair. The map is projected by
  // bin/experiment-slots.py into theme/snippets/experiment-slot-map.liquid
  // (a synchronous <script> setting window.NC_EXP_SLOTS), loaded GLOBALLY
  // in theme/layout/theme.liquid's <head> BEFORE this deferred asset — so it
  // is present on EVERY page. Its shape (ACTIVE slots only, keyed by the
  // experiment key; B2's projection carries the slot NUMBER so no dim-name
  // regex is needed here):
  //   window.NC_EXP_SLOTS = { "<key>": { slot:<n>, id_dim:"…", variant_dim:"…", scope:"…" }, … }
  //
  // slotDims(key) -> { slot, id_dim, variant_dim, scope } resolves a key to its
  // emit dim names. tagVariant/emitView land the payload under those dim names
  // instead of the hardcoded experiment_id/experiment_variant — for a slot-0
  // (or map-absent) experiment that is byte-identical to today; for a slot-K
  // experiment it writes exp_slotK_id / exp_slotK_variant.
  //
  // The nc_exp_active cookie (writeActiveCookie, below) is a per-page snapshot
  // the Customer Events pixel reads ONCE, in-handler, to tag the pixel-side
  // add_to_cart generically — it carries the DIM NAMES so the pixel needs NO
  // slot-grammar reconstruction. It is a functional / strictly-necessary cookie
  // (non-identifying variant tokens, set independent of consent), mirroring the
  // nc_exp_{key} durable cookie's attributes; nc_exp_active is a SEPARATE
  // additive snapshot cookie — the nc_exp_{key} assignment cookie (owned by
  // experiment-init.liquid) is unchanged.

  // Slot-0 legacy identity — the pre-registered experiment_id/experiment_variant
  // pair, reused as the lowest pool slot. It is the documented canonical DEFAULT
  // when the map is entirely absent (a canonical default, NOT a silent-fallback
  // violation per rules/no_fallbacks.md).
  var SLOT0_DIMS = {
    slot: 0,
    id_dim: 'experiment_id',
    variant_dim: 'experiment_variant',
    scope: undefined,
  };

  // slotDims(key) -> { slot, id_dim, variant_dim, scope }. Reads
  // window.NC_EXP_SLOTS[key] when present. Two distinct absent cases:
  //   (a) map ENTIRELY absent (should not happen — it loads globally in
  //       theme.liquid) → default to the slot-0 legacy identity.
  //   (b) map PRESENT but this key MISSING (a stale projection, a partial
  //       --only push, or the map asset failed to load) → console.warn (make
  //       the misroute VISIBLE instead of silent), THEN fall back to the
  //       slot-0 identity (least-bad; the report's exp_slot0_id==key filter
  //       still isolates it).
  function slotDims(key) {
    var map = window.NC_EXP_SLOTS;
    if (!map) return SLOT0_DIMS; // (a) map entirely absent → canonical slot-0 default
    var d = map[key];
    if (!d) {
      // (b) map present but key missing → stale projection; warn, then slot-0.
      try {
        console.warn('[experiment-core] "' + key + '" is active but absent from NC_EXP_SLOTS — stale projection; run bin/experiment-slots.py project + re-push');
      } catch (e) { /* console unavailable — best-effort */ }
      return SLOT0_DIMS;
    }
    return d;
  }

  // ─── tagVariant(): extend a CTA payload with the key's slot dims ───
  // Returns `payload` extended with { [id_dim]: key, [variant_dim]: <effective> }
  // where the dim NAMES come from slotDims(key) — the key's pre-registered GA4
  // slot pair (slot 0 = the legacy experiment_id/experiment_variant, so a
  // slot-0/map-absent experiment tags byte-identically to before; a slot-K
  // experiment tags exp_slotK_id/exp_slotK_variant). The variant is undefined
  // when the experiment is not running on this surface; the caller decides
  // whether to forward it. Because two concurrent experiments write DIFFERENT
  // dim names, the global tracker's active()-loop tagVariant MERGE (in
  // audible-tracking.js) no longer collides — that is what makes concurrent
  // audible_click tagging correct.
  //
  // (For a Shopify-standard event tagged pixel-side — `add_to_cart` — the params
  // are set IN-HANDLER from the `nc_exp_active` cookie, not via this DOM-reading
  // helper; documented in the pixel fan-out / Chunk D.)
  function tagVariant(key, payload) {
    var out = payload || {};
    var dims = slotDims(key);
    out[dims.id_dim] = key;
    out[dims.variant_dim] = effectiveVariant(key);
    return out;
  }

  // ─── experiment_view denominator emit ───
  // Emit ONE `experiment_view` per session per experiment key through the
  // canonical consent-gated NCCtaTracking.publish (the 3-sink chain — NOT a
  // re-inlined publish). This is the experiment's DENOMINATOR: without it the
  // report has no per-variant viewer base to divide the buy-click numerator by.
  //
  // Once-guard-after-consent: the sessionStorage guard `nc_exp_view_{key}` is set
  // ONLY when the publish actually went through (published === true). If consent
  // is not yet granted (banner still open → publish returns false) we do NOT set
  // the guard, so a later consented load in the same session can still fire the
  // denominator. Otherwise a pre-consent load would burn the guard while the event
  // was suppressed, producing a numerator user with no denominator membership.
  var VIEW_MAX_TRIES = 10;
  var VIEW_RETRY_MS = 300;

  // key -> last-requested extraPayload. Powers the late-consent re-fire below so a
  // visitor who accepts consent AFTER the bounded retry window still lands in the
  // denominator (idempotent via the once-guard).
  var pendingViews = {};

  function trackingReady() {
    // References Shopify.analytics.publish ONLY as a typeof readiness predicate —
    // it is never called from this asset (the sole emit is NCCtaTracking.publish).
    return !!(
      window.NCCtaTracking &&
      typeof window.NCCtaTracking.publish === 'function' &&
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    );
  }

  function emitViewInternal(key, extraPayload, tries) {
    try {
      // Re-check the once-guard on each retry: a consented emit on a prior tick
      // (or a parallel path) may have already set it.
      var guardKey = 'nc_exp_view_' + key;
      if (sessionStorage.getItem(guardKey)) return; // already counted this session

      var variant = effectiveVariant(key);
      if (variant === undefined) return; // experiment not running here — nothing to count

      if (!trackingReady()) {
        if (tries < VIEW_MAX_TRIES) {
          setTimeout(function () { emitViewInternal(key, extraPayload, tries + 1); }, VIEW_RETRY_MS);
        }
        return; // not ready yet (or gave up) — do NOT set the guard
      }

      // Spread the caller's extraPayload FIRST, then set the key's slot dims — so
      // the key's id_dim/variant_dim CANNOT be overridden by a caller (callers
      // pass surface context like page_path, never these two). For a slot-0 (or
      // map-absent) experiment these are experiment_id/experiment_variant, so the
      // emitted payload is byte-identical to today; a slot-K experiment lands on
      // exp_slotK_id/exp_slotK_variant.
      var payload = {};
      if (extraPayload) {
        for (var k in extraPayload) {
          if (Object.prototype.hasOwnProperty.call(extraPayload, k)) {
            payload[k] = extraPayload[k];
          }
        }
      }
      var dims = slotDims(key);
      payload[dims.id_dim] = key;
      payload[dims.variant_dim] = variant;

      var published = window.NCCtaTracking.publish('experiment_view', payload);

      // Set the guard ONLY after a CONSENTED publish. If consent was withheld
      // (published === false) leave the guard unset so a later consented load this
      // session can still fire the denominator.
      if (published === true) {
        try { sessionStorage.setItem(guardKey, '1'); } catch (e) { /* storage blocked — best-effort */ }
      }
    } catch (e) { /* never throw */ }
  }

  // emitView(key, extraPayload): fire (or begin the bounded retry for) the
  // per-experiment denominator. Records the request so the late-consent handler
  // can re-fire it.
  function emitView(key, extraPayload) {
    pendingViews[key] = extraPayload || null;
    emitViewInternal(key, extraPayload, 0);
  }

  // ─── Late-consent denominator re-fire ───
  // The bounded readiness-retry gives up after ~3s, so a visitor who ACCEPTS
  // consent AFTER that window would never fire experiment_view — yet their later
  // audible_click/add_to_cart WOULD fire, producing a numerator-user with no
  // denominator and tripping the report's `buy_users > viewers` data-integrity
  // gate (a measurement OUTAGE). Re-fire every requested view when Shopify's
  // page-context Customer Privacy API reports consent was (re)collected. Idempotent
  // via the nc_exp_view_{key} once-guard (a call after the guard is set is a
  // no-op → no double-fire); a REJECT leaves the guard unset (correctly, no
  // denominator for opt-outs). Consent-gated: emitViewInternal still publishes
  // through the userCanBeTracked()-gated NCCtaTracking.publish. Never throws.
  function refireViews() {
    for (var key in pendingViews) {
      if (Object.prototype.hasOwnProperty.call(pendingViews, key)) {
        emitViewInternal(key, pendingViews[key], 0);
      }
    }
  }

  try {
    document.addEventListener('visitorConsentCollected', refireViews);
  } catch (e) { /* never throw */ }

  // ─── nc_exp_active: per-page snapshot cookie for the pixel fan-out ───
  // The Customer Events pixel cannot read the <html> reveal classes, so the
  // pixel-side add_to_cart tags its variant from THIS cookie instead. Carrying
  // the DIM NAMES (id_dim/variant_dim) in the cookie makes the pixel fully
  // data-driven — it sets exactly those GA4 params with NO slot-grammar
  // reconstruction (the dim names are registry data flowing through the cookie).
  //
  // Value = encodeURIComponent(JSON.stringify(map)), keyed by the slot NUMBER:
  //   { "<slot>": { id:<key>, v:<effectiveVariant>, id_dim, variant_dim, scope } }
  // for every active experiment (active() + slotDims(key)). encodeURIComponent is
  // REQUIRED — the raw JSON ({, }, ",, ") is invalid in an un-encoded cookie value
  // (unlike the bare-token nc_exp_{key}). Empty page → encodeURIComponent('{}') so
  // the snapshot never goes stale within a page that runs this asset.
  //
  // Attributes MATCH nc_exp_{key}: path=/; SameSite=Lax; max-age=15552000 (~180d).
  // It is a FUNCTIONAL / strictly-necessary cookie — non-identifying variant
  // tokens only, set independent of consent (the same classification + rationale
  // as nc_exp_{key}).
  var ACTIVE_COOKIE_MAX_AGE = 15552000; // ~180 days, matching nc_exp_{key}
  function writeActiveCookie() {
    try {
      var running = active();
      var map = {};
      for (var i = 0; i < running.length; i++) {
        var key = running[i].key;
        var dims = slotDims(key);
        map[dims.slot] = {
          id: key,
          v: effectiveVariant(key),
          id_dim: dims.id_dim,
          variant_dim: dims.variant_dim,
          scope: dims.scope,
        };
      }
      var value = encodeURIComponent(JSON.stringify(map));
      document.cookie = 'nc_exp_active=' + value + '; path=/; SameSite=Lax; max-age=' + ACTIVE_COOKIE_MAX_AGE;
    } catch (e) { /* never throw */ }
  }

  // ─── Public registry ───
  window.NCExperiments = {
    get: get,
    active: active,
    emitView: emitView,
    tagVariant: tagVariant,
  };

  // Write the per-page snapshot cookie ONCE at install, after the registry is
  // assigned (active() reads the reveal classes the inline snippet already set
  // pre-paint). It deliberately does NOT run on the visitorConsentCollected
  // re-fire: the cookie is consent-INDEPENDENT (it carries only non-identifying
  // variant tokens), so its value does not change when consent is (re)collected —
  // re-writing it there would be a redundant no-op.
  writeActiveCookie();
})();
